import axios from 'axios';
import COLORS from '../colors/constants.js';
import { buildApiUrl } from '../../services/apiConfig';
import { v4 as uuidv4 } from 'uuid';

const API_BASE_URL = buildApiUrl('AUTH');

/**
 * Fetch user color preferences from the API
 * @param {string} userId - The user ID
 * @returns {Promise<Object>} - The color preferences or null if not found
 */
export const fetchUserColorPreferences = async (userId) => {
  try {
    if (!userId) return null;
    
    const response = await axios.get(`${API_BASE_URL}/api/user/color-preferences`, {
      headers: { 'user-id': userId }
    });
    
    if (response.data.preferences) {
      return {
        PRIMARY: response.data.preferences.primary_color,
        PRIMARY_LIGHT: response.data.preferences.primary_light,
        PRIMARY_DARK: response.data.preferences.primary_dark,
        SECONDARY: response.data.preferences.secondary_color,
        SECONDARY_LIGHT: response.data.preferences.secondary_light,
        SECONDARY_DARK: response.data.preferences.secondary_dark
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching user color preferences:', error);
    return null;
  }
};

/**
 * Save user color preferences to the API
 * @param {string} userId - The user ID
 * @param {Object} colorData - The color data to save
 * @returns {Promise<boolean>} - Whether the save was successful
 */
export const saveUserColorPreferences = async (userId, colorData) => {
  try {
    if (!userId) return false;
    
    const payload = {
      id: uuidv4(),
      primary_color: colorData.PRIMARY || colorData.primary_color,
      primary_light: colorData.PRIMARY_LIGHT || colorData.primary_light,
      primary_dark: colorData.PRIMARY_DARK || colorData.primary_dark,
      secondary_color: colorData.SECONDARY || colorData.secondary_color,
      secondary_light: colorData.SECONDARY_LIGHT || colorData.secondary_light,
      secondary_dark: colorData.SECONDARY_DARK || colorData.secondary_dark
    };
    
    await axios.post(`${API_BASE_URL}/api/user/color-preferences`, payload, {
      headers: {
        'user-id': userId,
        'Content-Type': 'application/json'
      }
    });
    
    return true;
  } catch (error) {
    console.error('Error saving user color preferences:', error);
    return false;
  }
};

/**
 * Delete user color preferences from the API
 * @param {string} userId - The user ID
 * @returns {Promise<boolean>} - Whether the delete was successful
 */
export const deleteUserColorPreferences = async (userId) => {
  try {
    if (!userId) return false;
    
    await axios.delete(`${API_BASE_URL}/api/user/color-preferences`, {
      headers: { 'user-id': userId }
    });
    
    return true;
  } catch (error) {
    console.error('Error deleting user color preferences:', error);
    return false;
  }
};

/**
 * Apply color settings to CSS variables
 * @param {Object} colorObj - The color object to apply
 */
export const applyColors = (colorObj) => {
  const root = document.documentElement;
  Object.entries(colorObj).forEach(([key, value]) => {
    if (value) {
      root.style.setProperty(`--color-${key.toLowerCase().replace(/_/g, '-')}`, value);
    }
  });
};

/**
 * Get default colors
 * @returns {Object} - The default colors
 */
export const getDefaultColors = () => {
  return COLORS;
};

export default {
  fetchUserColorPreferences,
  saveUserColorPreferences,
  deleteUserColorPreferences,
  applyColors,
  getDefaultColors
};